import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import {
  Breadcrumb,
  Button,
  Col,
  Input,
  Row,
  Space,
  Table,
  Tooltip,
  message,
  Checkbox,
  Menu,
  Dropdown,
  Pagination,
} from 'antd';
import { useState, useEffect, useRef } from 'react';
import dayjs from 'dayjs';
import { Link, useNavigate } from 'react-router-dom';
import { ReactComponent as Back } from '../../Images/Back.svg';
import { ReactComponent as ColumnList } from '../../Images/ColumnList.svg';
import { ReactComponent as EditIcon } from '../../Images/EditIcon.svg';
import { ReactComponent as FilterIcon } from '../../Images/FilterIcon.svg';
import { ReactComponent as ViewIcon } from '../../Images/ViewIcon.svg';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchUserGroupList,
  resetUserGroupList,
  archiveUserGroup,
} from '../../slices/user-group';

export default function ManageUserGroup() {
  let navigate = useNavigate();
  const dispatch = useDispatch();
  const { user: currentUser } = useSelector(state => state.auth);
  const { data = [], totalRecord, status} = useSelector(state => state.userGroup);
  const [search, setSearch] = useState(localStorage.getItem('manageUserGroup') || '');
  const [inputValue, setInputValue] = useState(localStorage.getItem('manageUserGroup') || '');
  const [orderBy, setOrderBy] = useState('');
  const [page, setPage] = useState(1);
  const [pagesize, setPageSize] = useState(20);
  const [dropdownVisible, setDropdownVisible] = useState(false);

  useEffect(() => {
    dispatch(resetUserGroupList());
    dispatch(
      fetchUserGroupList({
        page,
        pagesize,
        ...(search && { search: search }),
        ...(orderBy && { ordering: orderBy }),
      }),
    );
  }, [page, pagesize, search, orderBy]);



  const handleTableChange = (pagination, filters, sorter) => {
    if (sorter?.columnKey && sorter.order) {
      const orderBy = GetSortingEnumValue(sorter.columnKey, sorter.order);
      setOrderBy(orderBy);
    }
  };
  const handlePageSizeChange = (current, size) => {
    setPage(1); // Reset to first page if needed
    setPageSize(size); // You must have a setter or state update
  };
  const handleSearch = e => {
    const value = e.target.value;
    setInputValue(value);
    setSearch(value);
    localStorage.setItem('manageUserGroup', value);
  };
  const resetFilter = () => {
    setSearch('');
    setInputValue('');
    setOrderBy(null); // if applicable

    localStorage.removeItem('manageUserGroup');
    dispatch(fetchUserGroupList({ page: 1, pagesize }));
  };

  const handlePageChange = newPage => {
    setPage(newPage);
  };
  const GetSortingEnumValue = (sortField, sortOrder) => {
    let sortingEnumKey = 'id';

    if (sortField === 'name' && sortOrder === 'ascend') {
      sortingEnumKey = 'name';
    }
    if (sortField === 'name' && sortOrder === 'descend') {
      sortingEnumKey = '-name';
    }
    return sortingEnumKey;
  };

  const handleEdit = data => {
    navigate(`/edit-user-group/${data.id}`);
  };

  const handleDelete = id => {
    if (!id) {
      message.error('Missing ID for deletion!');
      return;
    }

    dispatch(archiveUserGroup(id))
      .unwrap()
      .then(response => {
        if (response && response.success) {
          message.success(response.success); // Display the API success message
        } else {
          message.success('Group deleted permanently.'); // Fallback message
        }
      })
      .catch(error => {
        if (typeof error === 'object' && error !== null) {
          const messages = [];
          for (const key in error) {
            if (Array.isArray(error[key])) {
              messages.push(`${key}: ${error[key].join(', ')}`);
            }
          }

          if (messages.length > 0) {
            message.error(messages.join('; '));
          } else {
            message.error('An unknown error occurred.');
          }
        } else if (typeof error === 'string') {
          message.error(error); // Handle string error
        } else if (error instanceof Error) {
          message.error(error.message); // Handle Error object
        } else {
          message.error('An unknown error occurred.');
        }
      });
  };

  const [checkedList, setCheckedList] = useState(['name', 'id', 'action', 'users']);

  const handleColumnVisibilityChange = checkedValues => {
    setCheckedList(checkedValues);
  };

  const columns = [
    {
      title: 'Sr. No.',
      dataIndex: 'id',
      key: 'id',
      render: (text, record, index) => (page - 1) * pagesize + index + 1,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Assigned Users',
      dataIndex: 'users',
      key: 'users',
      render: name => {
      return (
          <span>
            {name && name.length > 0 ? name.join(', ') : 'No users assigned'}
          </span>
        );
      },
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',

      render: record => {
        return (
          <span>
            {record ? dayjs(record).format('DD-MM-YYYY HH:mm') : 'N/A'}{' '}
          </span>
        );
      },
    },
    {
      title: 'Created By',
      dataIndex: 'created_by',
      key: 'created_by',

      render: record => {
        return (
          <span>
            {record !== null
              ? `${record.first_name} ${record.last_name}`
              : 'N/A'}{' '}
          </span>
        );
      },
    },
    {
      title: 'Updated At',
      dataIndex: 'updated_at',
      key: 'updated_at',

      render: record => {
        return (
          <span>
            {record && record.trim()
              ? dayjs(record).format('DD-MM-YYYY HH:mm')
              : 'N/A'}
          </span>
        );
      },
    },
    {
      title: 'Updated By',
      dataIndex: 'updated_by',
      key: 'updated_by',

      render: record => {
        return (
          <span>
            {record && record.first_name?.trim() && record.last_name?.trim()
              ? `${record.first_name} ${record.last_name}`
              : 'N/A'}
          </span>
        );
      },
    },
    {
      title: 'Action',
      dataIndex: 'id', // Use `id` here for proper alignment and access
      key: 'action',
      render: (text, record) => (
        <Space>
          {currentUser.permissions.permissions.user.includes(
            'view_customgroup',
          ) && (
              <Tooltip placement="top" title={'View User Group'}>
                <Link to={`/view-user-group/${record.id}`}>
                  <ViewIcon />
                </Link>
              </Tooltip>
            )}
          {currentUser.permissions.permissions.user.includes(
            'change_customgroup',
          ) && (
              <Tooltip placement="top" title={'Edit'}>
                <span onClick={() => handleEdit(record)}>
                  <EditIcon />
                </span>
              </Tooltip>
            )}
        </Space>
      ),
    },
  ];
  const filteredColumns = columns.filter(column =>
    checkedList.includes(column.key),
  );

  const columnMenu = (
    <Menu className="packagelistdropdown">
      <Menu.Item key="columnMenu">
        <Checkbox.Group
          value={checkedList}
          onChange={handleColumnVisibilityChange}
        >
          <Row>
            <Col span={12}>
              <Checkbox value="id" disabled>
                Sr. No.
              </Checkbox>
            </Col>
            <Col span={12}>
              <Checkbox value="name">Name</Checkbox>
            </Col>
            <Col span={12}>
              <Checkbox value="created_at">Created At</Checkbox>
            </Col>
            <Col span={12}>
              <Checkbox value="created_by">Created By</Checkbox>
            </Col>
            <Col span={12}>
              <Checkbox value="updated_at">Updated At</Checkbox>
            </Col>
            <Col span={12}>
              <Checkbox value="updated_by">Updated By</Checkbox>
            </Col>
            <Col span={12}>
              <Checkbox value="action" disabled>
                Action
              </Checkbox>
            </Col>
          </Row>
        </Checkbox.Group>
      </Menu.Item>
    </Menu>
  );
  return (
   <div className='mainwrap'>
      <div className="sticyaction">
        <Row
          className="pagenamerow mb-0"
          justify="space-between"
          align="middle"
        >
          <Col>
            <h2 style={{ margin: 0 }}>Groups</h2>
            <div className="bredcrumbwrp">
              <Link onClick={() => navigate(-1)} className="back">
                <Back /> BACK
              </Link>
              <Breadcrumb
                items={[
                  { title: <Link to="/dashboard">Home</Link> },
                  { title: 'Groups' },
                ]}
              />
            </div>
          </Col>
          <Col>
            <Space size="small">
              <Input
                placeholder="Search"
                className="searchbox"
                prefix={<SearchOutlined />}
                value={search}
                onChange={handleSearch}
                style={{ width: 200 }}
              />
              <Tooltip placement="top" title="Reset Filter">
                <Button
                  type="primary"
                  disabled={!search && !orderBy}
                  className="iconlink"
                  onClick={resetFilter}
                >
                  <FilterIcon />
                </Button>
              </Tooltip>
              {currentUser.permissions.permissions.user.includes(
                'add_customgroup',
              ) && (
                  <Link to="/add-user-group">
                    <Tooltip placement="top" title={'Add Group'}>
                      <Button type="primary" icon={<PlusOutlined />}>
                        Add Group
                      </Button>
                    </Tooltip>
                  </Link>
                )}

              <Tooltip placement="topLeft" title="Column Filter Options">
                <Dropdown
                  overlay={columnMenu}
                  trigger={['click']}
                  placement="bottomRight"
                  visible={dropdownVisible}
                  onVisibleChange={setDropdownVisible}
                >
                  <Button
                    type="primary"
                    icon={<ColumnList />}
                    onClick={e => e.preventDefault()}
                  />
                </Dropdown>
              </Tooltip>
            </Space>
          </Col>
        </Row>
      </div>
     <div className='tablewrp'>
          <Table
            dataSource={data}
            rowKey="id"
            columns={filteredColumns}
            loading={status === 'loading'}
            pagination={false}
            onChange={handleTableChange}
            scroll={{ x: '600px' }}
          />
        </div>
     
          <Pagination
            current={page}
            pageSize={pagesize}
            total={totalRecord}
            showSizeChanger
            onShowSizeChange={handlePageSizeChange}
            pageSizeOptions={["20", "50", "100"]}
            showTotal={total => `Total ${total} items`}
            onChange={handlePageChange}
            locale={{ items_per_page: 'Items / Page' }}
          />
        </div>
  
  );
}
